import { computed, onBeforeUnmount, onMounted, Ref, ref, watch } from 'vue';
import { cloneDeep, debounce } from 'lodash-es';
import KonvaService from '@/services/konva';
import { Shape, ShapeType } from '@/services/shape';
import { useEditorStore } from '@/store/use-editor-store';
import { useLayerStore } from '@/store/use-layer-store';
import { EventType } from '@/utils/emitter.utils';
import { useShapeManage } from './use-shape-manage';
import { useEmitterListener } from './use-event-listener';
import { KonvaDrag } from '@/services/konva/konva.drag';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { canUse } from '@/utils/can-use.utils';
import { useShapeCount } from './use-shape-count';

type AncestryInfo = {
  haslocked: boolean;
  hasInvisible: boolean;
  nodes: ShapeTreeNode[];
  notCurrSlotWidget?: boolean;
};

export const ICON_SRC: Record<ShapeType, string> = {
  [ShapeType.ProgressBar]: 'shape-progress.svg',
  [ShapeType.ImageArray]: 'shape-images.svg',
  [ShapeType.Image]: 'shape-image.svg',
  [ShapeType.Sprite]: 'shape-sprite.svg',
  [ShapeType.AppWidget]: 'shape-app-widget.svg',
  [ShapeType.JsFile]: 'shape-js.svg',
  [ShapeType.Text]: 'shape-text.svg',
  [ShapeType.Widget]: 'shape-group.svg',
  [ShapeType.Group]: 'shape-group.svg',
  [ShapeType.Slot]: 'shape-slot.svg',
  [ShapeType.PhotoAlbumBg]: 'shape-image.svg',
  [ShapeType.TextWatchface]: 'shape-text.svg',
  [ShapeType.Rive]: 'shape-app-widget.svg',
  [ShapeType.VideoWatchface]: 'shape-sprite.svg',
  [ShapeType.ApkFile]: 'shape-apk.svg'
};

export const shapeCnNameMap: Record<ShapeType, string> = {
  [ShapeType.Text]: '文本1',
  [ShapeType.ProgressBar]: '进度条1',
  [ShapeType.Image]: '图片1',
  [ShapeType.ImageArray]: '图组',
  [ShapeType.Slot]: '容器1',
  [ShapeType.Widget]: '组1',
  [ShapeType.Group]: '组1',
  [ShapeType.AppWidget]: '复杂功能1',
  [ShapeType.PhotoAlbumBg]: '相册/人像表盘背景',
  [ShapeType.JsFile]: 'JS/LUA程序1',
  [ShapeType.Sprite]: '序列帧1',
  [ShapeType.TextWatchface]: '可编辑文字',
  [ShapeType.Rive]: 'Rive动效1',
  [ShapeType.VideoWatchface]: '可编辑视频',
  [ShapeType.ApkFile]: 'APK/RPK程序1'
};

export const RED_TYPES = new Set<ShapeType>([ShapeType.Slot]);

export interface ShapeTreeNode {
  key: string;
  extra: {
    index: number;
    tag: ShapeType;
    name: string;
    locked: boolean;
    visibility: boolean;
    level: number;
    collapsed: boolean;
    notCurrSlotWidget?: boolean;
  };
  children?: ShapeTreeNode[];
  ancestryInfo?: AncestryInfo;
}

export const shapeTree: Ref<ShapeTreeNode[]> = ref([]);
/**
 * 当前渲染树中，已锁定的图层
 * 锁定规则：父级元素锁定或本身元素锁定
 */
export const lockedIdsInCurShapeTree: Ref<string[]> = ref([]);

const copiedItems = ref<
  {
    layer: StoreLayerItem;
    node: ShapeTreeNode;
  }[]
>([]);

export function useShapeTree(init = false) {
  const editorStore = useEditorStore();
  const layerStore = useLayerStore();
  const watchfaceStore = useWatchfaceStore();
  const { copyShape } = useShapeManage();
  const { validShapeCount } = useShapeCount();

  const generate = debounce(() => {
    const _generate = (
      layers: StoreLayerItem[],
      level: number,
      ancestryInfo: AncestryInfo | undefined,
      parent: StoreLayerItem | undefined,
      parents: StoreLayerItem[] | undefined
    ) => {
      const items2: ShapeTreeNode[] = [];
      const len = layers?.length ?? 0;
      for (let i = len - 1; i >= 0; i--) {
        const layer = layers[i];
        if (!layer) continue;

        const notCurrSlotWidget =
          ancestryInfo?.notCurrSlotWidget ||
          (parent?.type == ShapeType.Slot &&
            parent.attrs.widgetIdToDisplay != layer.id)
            ? true
            : false;
        const item2: ShapeTreeNode = {
          key: layer.id,
          extra: {
            index: i,
            tag: layer.type as ShapeType,
            locked: !!layer.attrs._isLocked,
            visibility: !!layer.attrs._visibility,
            name: layer.attrs.name ?? '',
            collapsed: !editorStore.layerTreeExpandkeys.includes(
              layer.id ?? ''
            ),
            level,
            notCurrSlotWidget
          }
        };

        if (ancestryInfo) {
          item2.ancestryInfo = ancestryInfo;
        }

        if (
          item2.extra.locked ||
          parents?.some((item) => !!item.attrs._isLocked)
        ) {
          lockedIdsInCurShapeTree.value.push(layer.id);
        }

        if (layer?.children?.length) {
          const res = _generate(
            layer?.children ?? [],
            level + 1,
            {
              haslocked: ancestryInfo?.haslocked || item2.extra.locked,
              hasInvisible:
                ancestryInfo?.hasInvisible || !item2.extra.visibility,
              nodes: [...(ancestryInfo?.nodes ?? []), item2],
              notCurrSlotWidget
            },
            layer,
            [...(parents ?? []), layer]
          );
          item2.children = res;
        }

        items2.push(item2);
      }
      return items2;
    };

    const currTheme = useEditorStore().theme;
    lockedIdsInCurShapeTree.value = [];
    const res = _generate(
      currTheme?.children ?? [],
      0,
      undefined,
      undefined,
      undefined
    );
    shapeTree.value = res;
    // 每次shape tree改变后，执行一下刷新
  }, 20);

  // 需要重新计算tree的事件
  const eventTypes: EventType[] = [
    EventType['watchface:opened'],
    EventType['shape:create'],
    EventType['shape:copy'],
    EventType['shape:deleted'],
    EventType['shape:drag'],
    EventType['shape:set-attr'],
    EventType['shape:widgeted'],
    EventType['shape:unwidgeted'],
    EventType['shape:sloted'],
    EventType['shape:unsloted'],
    EventType['theme:selected'],
    EventType['snapshot:restore']
  ];

  const expandAllowedShapes = new Set<ShapeType>([
    ShapeType.Widget,
    ShapeType.Slot,
    ShapeType.Group
  ]);

  onMounted(() => {
    if (!init) return;
    generate();
  });
  useEmitterListener(eventTypes, () => {
    if (!init) return;
    if (KonvaDrag.DRAGGING) return;
    generate();
  });

  watch(
    () => editorStore.layerTreeExpandkeys,
    () => {
      if (!init) return;
      generate();
    }
  );

  onBeforeUnmount(() => {
    if (!init) return;
    shapeTree.value = [];
  });

  const flatShapeTree = computed(() => {
    const shapes = [...shapeTree.value];
    const result = [];
    let node;

    while ((node = shapes.shift())) {
      result.push(node);
      if (node.children) {
        shapes.unshift(...node.children);
      }
    }
    return result;
  });

  const uncollapsedFlatShapeTree = computed<ShapeTreeNode[]>(() => {
    const shapes = [...shapeTree.value];
    const result = [];
    let node;

    while ((node = shapes.shift())) {
      result.push(node);
      if (node.children && !node.extra.collapsed) {
        shapes.unshift(...node.children);
      }
    }
    return result;
  });

  const _guide = computed<Map<string, number>>(() => {
    const map = new Map();
    flatShapeTree.value.forEach((node, index) => map.set(node.key, index));
    return map;
  });

  const _uncollapsedGuide = computed<Map<string, number>>(() => {
    const map = new Map();
    uncollapsedFlatShapeTree.value.forEach((node, index) =>
      map.set(node.key, index)
    );
    return map;
  });

  const hasPhotoAlbumBg = computed(() =>
    shapeTree.value.some((v) =>
      [
        ShapeType.PhotoAlbumBg,
        ShapeType.TextWatchface,
        ShapeType.VideoWatchface
      ].includes(v.extra.tag)
    )
  );

  const getNodeById = (id: string): ShapeTreeNode | null => {
    const index = _guide.value.get(id);
    return index !== undefined ? flatShapeTree.value[index] : null;
  };

  const getUncollapsedNodeById = (id: string): ShapeTreeNode | null => {
    const index = _uncollapsedGuide.value.get(id);
    return index !== undefined ? uncollapsedFlatShapeTree.value[index] : null;
  };

  const getParent = (node: ShapeTreeNode): ShapeTreeNode | null => {
    if (node.ancestryInfo?.nodes.length) {
      return node.ancestryInfo.nodes[node.ancestryInfo.nodes.length - 1];
    } else {
      return null;
    }
  };

  function getParentByPosition(
    index: number,
    level: number
  ): ShapeTreeNode | undefined {
    let parentIndex = index - 1;
    while (
      parentIndex >= 0 &&
      uncollapsedFlatShapeTree.value[parentIndex].extra.level >= level
    ) {
      parentIndex--;
    }

    return uncollapsedFlatShapeTree.value[parentIndex];
  }

  function getElderBrotherNumber(
    index: number,
    level: number,
    exceptKeys: string[] = []
  ): number {
    let elderBrotherNum = 0;

    for (let i = index; i < uncollapsedFlatShapeTree.value.length; i++) {
      if (uncollapsedFlatShapeTree.value[i].extra.level < level) {
        break;
      }
      if (
        uncollapsedFlatShapeTree.value[i].extra.level === level &&
        !exceptKeys.includes(uncollapsedFlatShapeTree.value[i].key)
      ) {
        elderBrotherNum++;
      }
    }

    return elderBrotherNum;
  }

  /**
   * 将节点 id 按 flatShapeTree 中的 index 从小到大排序，返回新数组
   * @param ids 需要被排序的节点 id 们
   */
  const getSortedIds = (ids: string[]): string[] => {
    return ids
      .filter((id) => _guide.value.has(id))
      .sort(
        (a, b) =>
          (_guide.value.get(a) as number) - (_guide.value.get(b) as number)
      );
  };

  /**
   * 目标是否在某些类型的节点内部
   * @param target 检测目标
   * @param ancestry 被检测的祖先类型集合
   */
  const isIn = (target: ShapeTreeNode, ancestry: Set<ShapeType>): boolean => {
    if (target.ancestryInfo?.nodes.length) {
      for (let i = 0; i < target.ancestryInfo.nodes.length; i++) {
        if (ancestry.has(target.ancestryInfo.nodes[i].extra.tag)) {
          return true;
        }
      }
    }
    return false;
  };

  /**
   * 获得目标的 index
   * @param target 被检测 index 的目标
   * @param relativeTo 想知道的 index 是相对于哪种树的
   */
  const getIndex = (
    target: ShapeTreeNode,
    relativeTo: 'flatShapeTree' | 'uncollapsedFlatShapeTree' | 'shapeTree'
  ): number => {
    switch (relativeTo) {
      case 'flatShapeTree':
        return _guide.value.get(target.key) ?? -1;
      case 'uncollapsedFlatShapeTree':
        return _uncollapsedGuide.value.get(target.key) ?? -1;
      default:
        return target.extra.index;
    }
  };

  /**
   * 获得目标相对于 uncollapsedFlatShapeTree 中所有 index 对应的可移动至的层级
   * @param target 被检测可移动至的所有层级的目标
   */
  const getAvailableMoveLevels = (target: ShapeTreeNode): Set<number>[] => {
    const isAncestryQualified = (father: ShapeTreeNode): boolean => {
      // 容器不能有祖先
      if (target.extra.tag === ShapeType.Slot) return false;
      // 其他可拖动元素都能进单纯组
      const nearestNongroupAncestry =
        father.extra.tag === ShapeType.Group
          ? getNearestNongroupAncestry(father)
          : father;
      if (!nearestNongroupAncestry) return true;

      switch (target.extra.tag) {
        // js程序最近非组祖先不为容器、表达式
        case ShapeType.JsFile:
        case ShapeType.ApkFile:
          return (
            nearestNongroupAncestry.extra.tag !== ShapeType.Widget &&
            nearestNongroupAncestry.extra.tag !== ShapeType.Slot
          );
        case ShapeType.AppWidget:
          return (
            nearestNongroupAncestry.extra.tag !== ShapeType.Widget &&
            nearestNongroupAncestry.extra.tag !== ShapeType.Slot
          );
        // 表达式父元素不做限制
        case ShapeType.Widget:
          return true;
        // 组的最近非组祖先不为容器，组里有js程序的话，最近非组祖先也不为表达式
        case ShapeType.Group:
          if (nearestNongroupAncestry.extra.tag === ShapeType.Slot) {
            return false;
          } else {
            const offspringTags = getOffspringTags(target);
            return (
              !offspringTags.has(ShapeType.JsFile) ||
              nearestNongroupAncestry.extra.tag !== ShapeType.Widget
            );
          }
        // 普通元素最近非组祖先不为容器
        default:
          return nearestNongroupAncestry.extra.tag !== ShapeType.Slot;
      }
    };

    let length = uncollapsedFlatShapeTree.value.length + 1;
    const result: Set<number>[] = new Array(length)
      .fill(undefined)
      .map(() => new Set());

    // 第一个图层一定为 0 级，且没有父亲就一定可以drop
    result[0].add(0);

    // 其他元素不许拖到相册表盘背景下面
    if (hasPhotoAlbumBg.value) {
      length--;
    }

    // 最后一个位置一定可以作为 level 0 添加
    result[length - 1].add(0);

    for (let i = 1; i < length; i++) {
      let notGreaterThanSelf = false;
      // 组不能拖拽到自己的内部
      if (expandAllowedShapes.has(target.extra.tag)) {
        const start = getIndex(target, 'uncollapsedFlatShapeTree') + 1;
        const end = getLastOffspringIndex(
          getIndex(target, 'uncollapsedFlatShapeTree'),
          'uncollapsedFlatShapeTree'
        );
        if (i >= start && i <= end) continue;
        if (i === end + 1) {
          notGreaterThanSelf = true;
        }
      }

      // 先看上方
      const last = uncollapsedFlatShapeTree.value[i - 1];

      let topLevel;
      if (
        (last.children || expandAllowedShapes.has(last.extra.tag)) &&
        !last.extra.collapsed
      ) {
        //如果是展开组，则可加到它的下一级
        if (isAncestryQualified(last)) {
          result[i].add(last.extra.level + 1);
        }
        topLevel = last.extra.level + 1;
      } else {
        // 其他情况，则可与上层层级统一
        const father = getParent(last);
        if (!father || isAncestryQualified(father)) {
          result[i].add(last.extra.level);
        }
        topLevel = last.extra.level;
      }

      // 再看下方
      const next = uncollapsedFlatShapeTree.value[i];
      let bottomLevel;
      if (next) {
        bottomLevel = next.extra.level;

        // 可以和下方元素保持一致
        const father = getParent(next);
        if (!father || isAncestryQualified(father)) {
          result[i].add(bottomLevel);
        }
      } else {
        bottomLevel = 0;
      }

      // 也可以在上下之间的数值里任选
      if (topLevel - bottomLevel > 1) {
        for (let j = bottomLevel + 1; j < topLevel; j++) {
          // 上面第一个比它level小的
          let k = i - 1;
          while (uncollapsedFlatShapeTree.value[k].extra.level >= j) {
            k--;
          }
          if (isAncestryQualified(uncollapsedFlatShapeTree.value[k])) {
            result[i].add(j);
          }
        }
      }

      if (notGreaterThanSelf) {
        result[i].forEach((v) => {
          if (v > target.extra.level) {
            result[i].delete(v);
          }
        });
      }
    }

    return result;
  };

  /**
   * 获得多个目标相对于 uncollapsedFlatShapeTree 中所有 index 对应的可移动至的层级
   * @param targets 被检测可移动至的所有层级的目标列表
   */
  const getAvailableMoveLevelsM = (targets: ShapeTreeNode[]): Set<number>[] => {
    const results = targets.map((target) => getAvailableMoveLevels(target));
    const intersectSets = (a: Set<number>, b: Set<number>) => {
      const c = new Set<number>();
      a.forEach((v) => b.has(v) && c.add(v));
      return c;
    };
    const result = [];
    for (let i = 0; i < uncollapsedFlatShapeTree.value.length + 1; i++) {
      const detect: Set<number>[] = [];
      for (let j = 0; j < targets.length; j++) {
        detect.push(results[j][i]);
      }
      result.push(detect.reduce(intersectSets));
    }
    return result;
  };

  /**
   * 获得目标的最远子孙范围
   * @param parentIndex 目标的 index
   */
  const getLastOffspringIndex = (
    parentIndex: number,
    relativeTo: 'flatShapeTree' | 'uncollapsedFlatShapeTree' = 'flatShapeTree'
  ): number => {
    const tree =
      relativeTo === 'flatShapeTree'
        ? flatShapeTree.value
        : uncollapsedFlatShapeTree.value;
    const parentLevel = tree[parentIndex].extra.level;
    let index = parentIndex;
    while (tree[index + 1] && tree[index + 1].extra.level > parentLevel) {
      index++;
    }
    return index;
  };

  function checkCanWidgeted(nodes: ShapeTreeNode[]): boolean {
    const curThemeType = editorStore.theme?.type;
    if (watchfaceStore.deviceType === 'M66' && curThemeType === 'AOD')
      return false;

    if (nodes.length === 0) return false;

    const exceptions = new Set<ShapeType>([
      ShapeType.Slot,
      ShapeType.PhotoAlbumBg,
      ShapeType.TextWatchface,
      ShapeType.JsFile,
      ShapeType.AppWidget,
      ShapeType.Rive,
      ShapeType.ApkFile
    ]);
    const firstNode = nodes[0];

    // 创建为表达式：选中项为同一层级，均非容器/相册表盘背景/js程序 且 如果是组的话里面没有容器/相册表盘背景/js程序
    if (
      nodes.some((node) => {
        return node.extra.level !== firstNode.extra.level;
      })
    )
      return false;

    if (
      nodes.some(
        (node) =>
          exceptions.has(node.extra.tag) ||
          (node.extra.tag === ShapeType.Group &&
            [...getOffspringTags(node)].some((tag) => exceptions.has(tag)))
      )
    )
      return false;

    return true;
  }

  function checkCanSloted(nodes: ShapeTreeNode[]): boolean {
    if (!canUse('shape-slot', watchfaceStore.deviceType).valid) return false;

    if (nodes.length === 0) return false;

    const firstNode = nodes[0];
    // 创建容器：选中项为同一层级，均为表达式，且不在容器/组/表达式里
    if (
      nodes.some((node) => {
        return node.extra.level !== firstNode.extra.level;
      })
    )
      return false;

    if (nodes.some((node) => node.extra.tag !== ShapeType.Widget)) return false;

    if (
      isIn(
        firstNode,
        new Set([ShapeType.Slot, ShapeType.Group, ShapeType.Widget])
      )
    )
      return false;

    return true;
  }

  function checkCanCopy(nodes: ShapeTreeNode[]): boolean {
    const canNotCopyTypes = [
      ShapeType.PhotoAlbumBg,
      ShapeType.TextWatchface,
      ShapeType.Rive
    ];
    // 确保每个类型都是可复制
    return nodes.every((node) => !canNotCopyTypes.includes(node.extra.tag));
  }

  /**
   * 返回目标节点最近的非组祖先
   * @param node 目标节点
   */
  const getNearestNongroupAncestry = (
    node: ShapeTreeNode
  ): ShapeTreeNode | null => {
    const nodes = node.ancestryInfo?.nodes;
    if (nodes) {
      for (let i = nodes.length - 1; i >= 0; i--) {
        if (nodes[i].extra.tag !== ShapeType.Group) {
          return nodes[i];
        }
      }
    }

    return null;
  };

  /**
   * 返回目标节点所有后代类型
   * @param node 目标节点
   */
  const getOffspringTags = (node: ShapeTreeNode): Set<ShapeType> => {
    const tags = new Set<ShapeType>();
    const index = getIndex(node, 'flatShapeTree');
    const lastIndex = getLastOffspringIndex(index, 'flatShapeTree');
    for (let i = index + 1; i <= lastIndex; i++) {
      tags.add(flatShapeTree.value[i].extra.tag);
    }
    return tags;
  };

  function copy(nodes: ShapeTreeNode[]) {
    if (checkCanCopy(nodes) === false) return;

    copiedItems.value = nodes
      .filter((node) => node.extra.tag !== ShapeType.PhotoAlbumBg)
      .reduce(
        (arr, node) => {
          const layer = layerStore.findLayer(node.key)?.layer;

          if (layer) {
            // 获取绝对位置
            const service = KonvaService.getIns();
            const { x = 0, y = 0 } = service?.getClientRect(layer.id) ?? {};
            const newLayer = cloneDeep(layer);

            newLayer.attrs.x = x;
            newLayer.attrs.y = y;

            arr.push({
              node: cloneDeep(node),
              layer: newLayer
            });
          }
          return arr;
        },
        [] as {
          layer: StoreLayerItem;
          node: ShapeTreeNode;
        }[]
      );
  }

  function cut(nodes: ShapeTreeNode[]) {
    copy(nodes);
    // 删除选中的元素
    nodes
      .filter((node) => node.extra.tag !== ShapeType.PhotoAlbumBg)
      .forEach((node) => Shape.getShape(node.key)?.destroy());
  }

  async function paste() {
    if (!(await validShapeCount())) return;
    if (copiedItems.value.length === 0) return;

    const index = getIndex(
      copiedItems.value[0].node,
      'uncollapsedFlatShapeTree'
    );
    const level = getNodeById(copiedItems.value[0].node.key)?.extra.level;
    let parentId;
    let position = getElderBrotherNumber(0, 0);

    // 判断放在第一个榜样前面还是所有图层前面
    if (index > 0 && level !== undefined) {
      const availableLevels = getAvailableMoveLevelsM(
        copiedItems.value.map((v) => v.node)
      );

      if (availableLevels[index].has(level)) {
        // 放在最上面的榜样上面，同一层级
        parentId = getParentByPosition(index, level)?.key;
        position = getElderBrotherNumber(index, level);
      }
    }

    const newIds: string[] = [];

    for (let i = 0; i < copiedItems.value.length; i++) {
      const copidItem = copiedItems.value[i];
      const newShape = copyShape(
        copidItem.layer,
        editorStore.themeId,
        false,
        parentId,
        position
      );

      newIds.push(newShape.id);
    }

    editorStore.select(newIds);
  }

  return {
    copiedItems,
    expandAllowedShapes,
    flatShapeTree,
    hasPhotoAlbumBg,
    shapeTree,
    uncollapsedFlatShapeTree,
    checkCanSloted,
    checkCanWidgeted,
    checkCanCopy,
    copy,
    cut,
    isIn,
    getAvailableMoveLevels,
    getAvailableMoveLevelsM,
    getElderBrotherNumber,
    getIndex,
    getLastOffspringIndex,
    getNodeById,
    getOffspringTags,
    getParent,
    getParentByPosition,
    getSortedIds,
    getUncollapsedNodeById,
    paste,
    update: generate
  };
}
